/**
 * Policy Management API Service
 */

import { apiClient } from './client';
import { getConfig } from '../config';

export interface Policy {
  id: string;
  type: string; // 'Terms' | 'Privacy' | 'Grievance'
  version: string;
  contentHash: string;
  documentAvailable: boolean;
  documentSize: number;
  active: boolean;
  createdAt: string;
  createdBy: string;
}

export interface CreatePolicyRequest {
  type: 'Terms' | 'Privacy' | 'Grievance';
  version: string;
  contentHash: string;
  policyDocument: File;
  createdBy: string;
}


/**
 * Policy Service
 */
export class PolicyService {
  /**
   * Get all policies
   */
  async getAllPolicies(): Promise<Policy[]> {
    try {
      console.log('[PolicyService] Fetching all policies');
      const response = await apiClient.get<Policy[]>('/api/policies');

      const policies = Array.isArray(response.data) ? response.data : [];
      console.log('[PolicyService] ✅ Policies fetched successfully', { count: policies.length });
      return policies;
    } catch (error) {
      console.error('[PolicyService] Failed to fetch policies', error);
      throw error;
    }
  }

  /**
   * Get latest policy by type
   */
  async getLatestPolicy(type: 'TERMS' | 'PRIVACY' | 'GRIEVANCE'): Promise<Policy | null> {
    try {
      console.log('[PolicyService] Fetching latest policy', { type });

      // Map to API enum values
      const typeMap: Record<string, string> = {
        'TERMS': 'Terms',
        'PRIVACY': 'Privacy',
        'GRIEVANCE': 'Grievance'
      };
      const apiType = typeMap[type] || type;

      const response = await apiClient.get<Policy>(`/api/policies/latest/${apiType}`);

      const policy = response.data || null;
      console.log('[PolicyService] ✅ Latest policy fetched', { type, hasPolicy: !!policy });
      return policy;
    } catch (error: any) {
      // Don't log error for 404 (no policy exists yet) or 401 (auth issues) - just return null
      if (error.response?.status === 404 || error.response?.status === 401 || error.response?.status === 500) {
        console.log('[PolicyService] No policy found or access denied', { type, status: error.response?.status });
        return null;
      }
      console.error('[PolicyService] Failed to fetch latest policy', error);
      return null;
    }
  }

  /**
   * Create new policy with file upload
   */
  async createPolicy(type: string, file: File, createdBy: string): Promise<Policy> {
    try {
      console.log('[PolicyService] Creating new policy', { type, fileName: file.name });

      // Map type to API enum values
      const typeMap: Record<string, string> = {
        'TERMS': 'Terms',
        'PRIVACY': 'Privacy',
        'GRIEVANCE': 'Grievance'
      };
      const apiType = typeMap[type] || type;

      // Generate SHA-256 hash of file content
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contentHash = 'sha256:' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Auto-generate version (API will handle this, but we'll send a placeholder)
      const version = new Date().getTime().toString();

      const formData = new FormData();
      formData.append('type', apiType);
      formData.append('version', version);
      formData.append('contentHash', contentHash);
      formData.append('policyDocument', file);
      formData.append('createdBy', createdBy);

      const response = await apiClient.post<Policy>('/api/policies', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const policy = response.data;
      console.log('[PolicyService] ✅ Policy created successfully', { id: policy.id, type: policy.type });
      return policy;
    } catch (error) {
      console.error('[PolicyService] Failed to create policy', error);
      throw error;
    }
  }

  /**
   * Download policy document
   */
  async downloadPolicy(policyId: string): Promise<Blob> {
    try {
      console.log('[PolicyService] Downloading policy document', { policyId });

      const response = await apiClient.get(`/api/policies/${policyId}/document`, {
        responseType: 'blob',
      });

      console.log('[PolicyService] ✅ Policy document downloaded successfully');
      return response.data;
    } catch (error) {
      console.error('[PolicyService] Failed to download policy document', error);
      throw error;
    }
  }

  /**
   * Get policy document URL for iframe/embed
   */
  getPolicyDocumentUrl(policyId: string): string {
    const config = getConfig();
    const baseUrl = config.apiBaseUrl;
    console.log('[PolicyService] Generating document URL', { policyId, baseUrl });
    return `${baseUrl}/api/policies/${policyId}/document`;
  }

  /**
   * Record policy acceptance by a user/shop
   */
  async acceptPolicy(shopId: string, policyId: string, acceptedByUserId: string): Promise<void> {
    try {
      const payload = {
        shopId,
        policyId,
        acceptedByUserId,
      };

      console.log('[PolicyService] 📝 Recording policy acceptance');
      console.log('[PolicyService] 📦 Request payload:', JSON.stringify(payload, null, 2));
      console.log('[PolicyService] 🔍 Payload details:', {
        shopId: `${shopId} (${typeof shopId})`,
        policyId: `${policyId} (${typeof policyId})`,
        acceptedByUserId: `${acceptedByUserId} (${typeof acceptedByUserId})`,
      });

      const response = await apiClient.post('/api/policy/acceptance', payload);

      console.log('[PolicyService] ✅ Policy acceptance recorded successfully');
      console.log('[PolicyService] 📡 Response:', response.data);
    } catch (error) {
      console.error('[PolicyService] ❌ Failed to record policy acceptance');
      console.error('[PolicyService] 💥 Error details:', error);

      // Log response error details if available
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error('[PolicyService] 📛 Backend error response:', {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
        });
      }

      throw error;
    }
  }
}

// Export singleton instance
export const policyService = new PolicyService();

